// src/components/ManuscriptCard.jsx
import React, { useEffect, useState, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { thumbUrlOf, titleOf } from "../lib/omekaClient";
import { useTranslation } from "react-i18next";

/** simple cache กันยิง thumbUrlOf ซ้ำ */
const msThumbCache = new Map();

/** * การ์ดสำหรับ Manuscript
 * ✅ เพิ่ม prop `variant` เพื่อรองรับสัดส่วนที่แตกต่างกัน
 * ✅ รองรับการ override ด้วย h-full หรือ class อื่นๆ
 */
function ManuscriptCardInner({ item, onClick, variant = "portrait", className = "" }) {
  const { t } = useTranslation();

  const [thumb, setThumb] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ กำหนดสัดส่วนใหม่ให้ยาวขึ้นและดูสมจริง
  const variantStyles = {
    portrait: "aspect-[3/4]",      // แนวตั้ง (หนังสือทั่วไป)
    palmleaf: "aspect-[5/1] md:aspect-[6/1]", // แนวยาวมาก (ใบลาน)
    samutthai: "aspect-[3/1] md:aspect-[4/1]" // แนวยาวปานกลาง (สมุดไทย)
  };

  const aspectClass = variantStyles[variant] || variantStyles.portrait;

  // ตรวจสอบว่าภายนอกส่งความสูง (h-) มาทับสัดส่วนเดิมหรือไม่
  const hasHeightOverride = className.includes('h-');
  // ถ้ามีการส่ง h- มา ให้ยกเลิก aspect ratio เดิม เพื่อให้มันขยายได้อิสระ
  const finalAspectClass = hasHeightOverride ? '' : aspectClass;

  useEffect(() => {
    let alive = true;

    if (!item) {
      setThumb(null);
      setLoading(false);
      return;
    }

    const run = async () => {
      setLoading(true);
      const id = item?.["o:id"];

      if (id && msThumbCache.has(id)) {
        if (!alive) return;
        setThumb(msThumbCache.get(id) || null);
        setLoading(false);
        return;
      }

      const url = await thumbUrlOf(item);
      if (!alive) return;

      if (id) msThumbCache.set(id, url || null);
      setThumb(url || null);
      setLoading(false);
    };

    run();

    return () => {
      alive = false;
    };
  }, [item]);

  const title = titleOf?.(item) || t("manuscriptCard.defaultTitle");

  return (
    <motion.button
      whileHover={{ y: -6, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      type="button"
      onClick={onClick}
      // ✅ ใช้ finalAspectClass และต่อท้ายด้วย className จากภายนอก
      className={`group relative w-full overflow-hidden rounded-xl shadow-md bg-[#f1e6da] border border-[#e5d7ca] hover:shadow-xl transition-all duration-500 ${finalAspectClass} ${className}`}
      title={t("manuscriptCard.openReaderTitle", { title })}
      aria-label={t("manuscriptCard.ariaLabel", { title })}
    >
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full h-full animate-pulse bg-[#e5d7ca]"
          />
        ) : thumb ? (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative w-full h-full"
          >
            <img
              src={thumb}
              alt={t("manuscriptCard.imageAlt", { title })}
              // ✅ ตรวจสอบว่า object-cover ครบถ้วน เพื่อให้รูปขยายเต็มไม่ว่าจะสัดส่วนไหน
              className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700 ease-out"
              loading="lazy"
              decoding="async"
              draggable="false"
            />

            {/* --- ส่วนหนึ่งใน ManuscriptCard.jsx --- */}

            {/* 1. ปรับ Overlay ให้แสดงตลอดเวลาเพื่อให้ตัวหนังสืออ่านง่าย */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#3d342e]/90 via-transparent to-transparent opacity-100 transition-opacity duration-500" />

            {/* 2. ปรับข้อความ Title ให้ตำแหน่งคงที่ ไม่ต้องรอ Hover */}
            <div className="absolute bottom-0 inset-x-0 p-3 translate-y-0 transition-transform duration-500">
              <p className="text-[12px] sm:text-sm font-bold text-white tracking-wider line-clamp-1 italic drop-shadow-md">
                {title}
              </p>
            </div>

            {/* Spine Effect: ปรับแต่งให้แสดงแค่ตอนเป็นแนวตั้ง */}
            {variant === "portrait" && (
              <div className="absolute inset-y-0 left-0 w-1.5 bg-black/20 blur-[1px]" />
            )}
          </motion.div>
        ) : (
          <div className="w-full h-full grid place-items-center bg-[#f1e6da] text-[#9a8a7e] text-[10px] font-bold uppercase tracking-tighter">
            {t("manuscriptCard.noImage")}
          </div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

export default memo(ManuscriptCardInner);